// src/components/Gojo3DScene.jsx
import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";

const Gojo3DScene = () => {
  const containerRef = useRef(null);
  const modelRef = useRef(null);
  const mixerRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const targetRotationRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    let scene, camera, renderer;
    let model;

    // Initialize scene
    scene = new THREE.Scene();

    // Camera setup - adjusted for side pose view
    camera = new THREE.PerspectiveCamera(
      15, // Narrower field of view for face focus
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.z = 15; // Further back to see more of the face
    camera.position.y = 0; // Centered vertically
    camera.position.x = 4; // More offset to the right to frame the moved model

    // Renderer
    renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true,
      preserveDrawingBuffer: true,
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio > 1 ? 2 : 1);
    renderer.setClearColor(0x000000, 0); // Transparent background
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    const containerEl = containerRef.current;
    if (containerEl) {
      containerEl.appendChild(renderer.domElement);
    }
    
    // Debug info
    console.log("3D Scene initialized");
    console.log("Container:", containerRef.current);
    console.log("Renderer canvas:", renderer.domElement);

    // Lighting - very subtle and clean, minimal shine
    const ambientLight = new THREE.AmbientLight(0xffffff, 1.0);
    scene.add(ambientLight);

    // Very soft directional light for minimal helmet highlight
    const helmetLight = new THREE.DirectionalLight(0xffffff, 1.2);
    helmetLight.position.set(2, 2, 5); // Positioned to hit the helmet
    scene.add(helmetLight);

    // Very soft fill light from the side
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.8);
    fillLight.position.set(-1, 0, 2);
    scene.add(fillLight);

    // Mouse movement handler for subtle side pose movement
    const handleMouseMove = (event) => {
      mouseRef.current.x = (event.clientX / window.innerWidth) * 2 - 1;
      mouseRef.current.y = -(event.clientY / window.innerHeight) * 2 + 1;
      
      // Subtle movement for side pose - reduced sensitivity
      targetRotationRef.current.y = mouseRef.current.x * 0.15; // Reduced for side view
      targetRotationRef.current.x = mouseRef.current.y * 0.1; // Reduced for side view
    };

    // Helper: apply viewport-specific tuning (mobile vs desktop)
    const applyViewportTuning = () => {
      if (!camera || !modelRef.current) return;
      const isMobile = window.innerWidth <= 768;
      const m = modelRef.current;

      if (isMobile) {
        // Bring face into view and rotate further to the left on small screens
        camera.fov = 18;
        camera.position.z = 13;
        camera.position.x = 3.5;
        camera.position.y = 0;
        camera.updateProjectionMatrix();

        m.scale.set(12, 12, 12);
        m.position.y = -36; // lowered slightly so face stays in frame on small screens
        m.position.x = 6; // a bit less than desktop so face centers better
        m.position.z = -6;
        m.rotation.y = Math.PI / 2 + 0.5; // rotate more to the left
        m.rotation.x = 0.12;
      } else {
        // Desktop defaults
        camera.fov = 15;
        camera.position.z = 15;
        camera.position.y = 0;
        camera.position.x = 4;
        camera.updateProjectionMatrix();

        m.scale.set(12, 12, 12);
        m.position.y = -35;
        m.position.x = 8;
        m.position.z = -8;
        m.rotation.y = Math.PI / 2 + 0.2;
        m.rotation.x = 0.15;
      }
    };

    // Load model
    const loader = new GLTFLoader();
    loader.load("/models/spartan.glb", (gltf) => {
      model = gltf.scene;

      // Initial desktop defaults; will be adjusted by applyViewportTuning for mobile
      model.scale.set(12, 12, 12);
      model.position.y = -35;
      model.position.x = 8;
      model.position.z = -8;
      model.rotation.y = Math.PI / 2 + 0.2;
      model.rotation.x = 0.15;
      scene.add(model);
      modelRef.current = model;

      // Animation mixer
      if (gltf.animations?.length) {
        const mixer = new THREE.AnimationMixer(model);
        mixer.clipAction(gltf.animations[0])?.play();
        mixerRef.current = mixer;
      }

      // Model is now static - no floating animation
      console.log("3D Model loaded successfully!");

      // Ensure the face is framed properly per viewport
      applyViewportTuning();
    }, 
    // Progress callback
    (progress) => {
      console.log("Loading progress:", (progress.loaded / progress.total * 100) + "%");
    },
    // Error callback
    (error) => {
      console.error("Error loading 3D model:", error);
    });

    // Animation loop
    const clock = new THREE.Clock();
    const animate = () => {
      requestAnimationFrame(animate);
      const delta = clock.getDelta();
      
      // Update model rotation based on mouse position
      if (modelRef.current) {
        const model = modelRef.current;
        const currentRotation = model.rotation;
        const targetRotation = targetRotationRef.current;
        
        // Smooth interpolation for rotation
        currentRotation.y += (targetRotation.y - currentRotation.y) * 0.02;
        currentRotation.x += (targetRotation.x - currentRotation.x) * 0.02;
      }
      
      if (mixerRef.current) mixerRef.current.update(delta);
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
      // Re-apply viewport tuning on resize
      applyViewportTuning();
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("mousemove", handleMouseMove);
    
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("mousemove", handleMouseMove);
      if (containerEl && renderer?.domElement && containerEl.contains(renderer.domElement)) {
        containerEl.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      id="container3D"
      className="w-full h-screen absolute top-0 right-0 z-0 pointer-events-none"
      style={{
        zIndex: 1,
        filter: "none"
      }}
    />
  );
};

export default Gojo3DScene;
